import type { LrcMetadata } from './types';
import { TS_REGEX } from './constants';

/**
 * Standard LRC ID metadata tag keys in recommended canonical order.
 */
export const STANDARD_METADATA_KEYS = [
  'ti',
  'ar',
  'al',
  'au',
  'length',
  'by',
  'offset',
  're',
  've'
] as const;

export type StandardMetadataKey = (typeof STANDARD_METADATA_KEYS)[number];

/**
 * Human-friendly labels and descriptions for standard LRC ID tags.
 */
export const METADATA_FIELDS_CONFIG: Array<{
  key: StandardMetadataKey;
  label: string;
  placeholder: string;
  hint: string;
}> = [
  { key: 'ti', label: 'Song Title', placeholder: 'e.g. Bohemian Rhapsody', hint: '[ti:Title]' },
  { key: 'ar', label: 'Artist', placeholder: 'e.g. Queen', hint: '[ar:Artist]' },
  { key: 'al', label: 'Album', placeholder: 'e.g. A Night at the Opera', hint: '[al:Album]' },
  { key: 'au', label: 'Author / Lyricist', placeholder: 'e.g. Freddie Mercury', hint: '[au:Lyricist]' },
  { key: 'length', label: 'Track Length', placeholder: 'e.g. 05:55', hint: '[length:mm:ss]' },
  { key: 'by', label: 'LRC Creator', placeholder: 'e.g. John Doe', hint: '[by:Creator]' },
  { key: 'offset', label: 'Time Offset (±ms)', placeholder: 'e.g. 0, +500, -200', hint: '[offset:+/-ms]' }
];

/**
 * Regex matching standard ID tags: [tag:value]
 * Tag key must start with a letter and contain only alphanumeric/underscore characters (2-12 chars).
 * Negative lookahead ensures timestamps (starting with digits) are never matched.
 */
export const METADATA_LINE_REGEX = /^\[([a-zA-Z][a-zA-Z0-9_-]{0,15})\s*:\s*(.*)\]$/;

/**
 * Parses all metadata ID tags from raw LRC text.
 * Separates metadata tags from lyric/timestamp lines.
 *
 * @param rawText Full raw LRC text.
 */
export function parseMetadata(rawText: string): {
  metadata: LrcMetadata;
  nonMetadataLines: string[];
  tagCount: number;
  rawTags: Array<{ tag: string; value: string }>;
} {
  const metadata: LrcMetadata = {};
  const nonMetadataLines: string[] = [];
  const rawTags: Array<{ tag: string; value: string }> = [];

  if (!rawText) {
    return { metadata, nonMetadataLines, tagCount: 0, rawTags };
  }

  const lines = rawText.split(/\r?\n/);

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      nonMetadataLines.push(rawLine);
      continue;
    }

    // Skip timestamped lyric lines
    if (TS_REGEX.test(trimmed)) {
      // Reset TS_REGEX lastIndex since it is global
      TS_REGEX.lastIndex = 0;
      nonMetadataLines.push(rawLine);
      continue;
    }
    TS_REGEX.lastIndex = 0;

    const match = trimmed.match(METADATA_LINE_REGEX);
    if (match && match[1] !== undefined && match[2] !== undefined) {
      const rawKey = match[1];
      const normalizedKey = rawKey.toLowerCase();
      const value = match[2].trim();

      metadata[normalizedKey] = value;
      rawTags.push({ tag: normalizedKey, value });
    } else {
      nonMetadataLines.push(rawLine);
    }
  }

  // Count distinct populated tags
  const tagCount = Object.keys(metadata).filter((k) => metadata[k]?.trim()).length;

  return {
    metadata,
    nonMetadataLines,
    tagCount,
    rawTags
  };
}

/**
 * Formats a metadata object into an array of canonical LRC ID tag lines.
 * Non-standard tags are preserved and appended after standard tags.
 *
 * @param metadata Metadata key-value map.
 */
export function formatMetadataHeaders(metadata: LrcMetadata): string[] {
  const headers: string[] = [];
  const seenKeys = new Set<string>();

  // 1. Output standard tags in canonical order
  for (const key of STANDARD_METADATA_KEYS) {
    const val = metadata[key]?.trim();
    if (val !== undefined && val !== '') {
      headers.push(`[${key}:${val}]`);
      seenKeys.add(key);
    }
  }

  // 2. Output any custom tags in alphabetical order
  const customKeys = Object.keys(metadata)
    .filter((k) => !seenKeys.has(k.toLowerCase()) && metadata[k]?.trim())
    .sort();

  for (const key of customKeys) {
    const val = metadata[key]?.trim();
    if (val) {
      headers.push(`[${key.toLowerCase()}:${val}]`);
    }
  }

  return headers;
}

/**
 * Replaces or injects metadata headers at the top of an LRC string while keeping
 * all timed lyrics and formatting intact.
 *
 * @param rawText Existing LRC text.
 * @param metadata New metadata to apply.
 */
export function applyMetadataToLrc(rawText: string, metadata: LrcMetadata): string {
  const { nonMetadataLines } = parseMetadata(rawText);
  const newHeaders = formatMetadataHeaders(metadata);

  // Trim leading empty lines from non-metadata content
  let startIndex = 0;
  while (startIndex < nonMetadataLines.length && !nonMetadataLines[startIndex]?.trim()) {
    startIndex++;
  }
  const cleanBody = nonMetadataLines.slice(startIndex).join('\n');

  if (newHeaders.length === 0) {
    return cleanBody;
  }

  if (!cleanBody.trim()) {
    return newHeaders.join('\n');
  }

  return `${newHeaders.join('\n')}\n${cleanBody}`;
}

/**
 * Deduplicates, normalizes tag keys to lowercase, sorts in canonical order,
 * and ensures all metadata headers are placed neatly at the top of the LRC text.
 *
 * @param rawText Existing LRC text.
 */
export function cleanAndSortMetadataHeaders(rawText: string): string {
  const parsed = parseMetadata(rawText);
  return applyMetadataToLrc(rawText, parsed.metadata);
}

/**
 * Counts populated metadata tags in raw LRC text without full extraction overhead.
 *
 * @param rawText Existing LRC text.
 */
export function countMetadataTags(rawText: string): number {
  return parseMetadata(rawText).tagCount;
}
